/**
 * SOPRunnerPanel — floating, draggable, resizable panel that guides a user
 * through a SOP run. Two view modes:
 *   • Step mode — one step at a time (guided)
 *   • Full mode — all steps as a table with notes + action buttons per row
 * Tracks per-step start/complete timing and shows a live run timer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fmtDuration } from "@/lib/format";
import { RichTextDisplay } from "@/components/RichTextEditor";
import {
  X, Minus, Maximize2, ChevronLeft, ChevronRight, Check, SkipForward, Upload,
  ExternalLink, Play, MessageSquare, UserPlus, GripVertical, Timer, List, Layers,
  Clock,
} from "lucide-react";

const MIN_W = 380;
const MIN_H = 320;
const DEFAULT_W = 680;
const DEFAULT_H = 580;

export default function SOPRunnerPanel({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [comment, setComment] = useState("");
  const [mode, setMode] = useState(() => localStorage.getItem("acquilix.sopRunner.mode") || "step");
  const [rowNotes, setRowNotes] = useState({});
  const [tick, setTick] = useState(0);
  const startedRef = useRef(new Set());
  const nav = useNavigate();

  useEffect(() => { localStorage.setItem("acquilix.sopRunner.mode", mode); }, [mode]);

  // Position + size state (persisted in localStorage per-user)
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem("acquilix.sopRunner.pos");
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return { x: window.innerWidth - DEFAULT_W - 24, y: 80, w: DEFAULT_W, h: DEFAULT_H };
  });
  useEffect(() => {
    try { localStorage.setItem("acquilix.sopRunner.pos", JSON.stringify(pos)); } catch { /* noop */ }
  }, [pos]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sop-runs/${runId}`);
      setRun(data.run);
      setSteps(data.steps || []);
      const firstPending = (data.steps || []).findIndex((s) => (s.completion?.status || "pending") === "pending");
      if (firstPending >= 0) setActiveIdx(firstPending);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load run"));
    }
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  // Live run timer (1s tick while in progress)
  useEffect(() => {
    if (run?.status !== "in_progress") return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [run?.status]);

  const elapsed = useMemo(() => {
    if (!run?.started_at) return null;
    const end = run.completed_at ? new Date(run.completed_at) : new Date();
    return Math.floor((end - new Date(run.started_at)) / 1000);
  }, [run, tick]); // eslint-disable-line

  // Mark step start when it becomes the active pending step
  const step = steps[activeIdx];
  useEffect(() => {
    const c = step?.completion;
    if (!c?.id || c.status !== "pending" || c.started_at || startedRef.current.has(c.id)) return;
    startedRef.current.add(c.id);
    api.post(`/sop-step-completions/${c.id}/start`).catch(() => {});
  }, [step]);

  // ── Drag ────────────────────────────────────────────────────────
  const dragRef = useRef(null);
  const onDragStart = (e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const { x, y } = pos;
    const move = (ev) => {
      setPos((p) => ({
        ...p,
        x: Math.max(0, Math.min(window.innerWidth - 100, x + (ev.clientX - startX))),
        y: Math.max(0, Math.min(window.innerHeight - 60, y + (ev.clientY - startY))),
      }));
    };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // ── Resize ──────────────────────────────────────────────────────
  const onResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const { w, h } = pos;
    const move = (ev) => {
      setPos((p) => ({
        ...p,
        w: Math.max(MIN_W, Math.min(window.innerWidth - p.x - 12, w + (ev.clientX - startX))),
        h: Math.max(MIN_H, Math.min(window.innerHeight - p.y - 12, h + (ev.clientY - startY))),
      }));
    };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // ── Actions ─────────────────────────────────────────────────────
  const doneCount = useMemo(
    () => steps.filter((s) => ["done", "skipped"].includes(s.completion?.status)).length,
    [steps],
  );
  const progress = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  const nextPendingIdx = (list, fromIdx) => {
    for (let i = fromIdx + 1; i < list.length; i++) {
      if ((list[i].completion?.status || "pending") === "pending") return i;
    }
    const first = list.findIndex((s) => (s.completion?.status || "pending") === "pending");
    return first >= 0 ? first : fromIdx;
  };

  const submitFor = async (target, status, note) => {
    const cid = target?.completion?.id;
    if (!cid) { toast.error("Missing completion id"); return; }
    setBusy(true);
    try {
      await api.post(`/sop-step-completions/${cid}`, { status, comment: note || null });
      const { data } = await api.get(`/sop-runs/${runId}`);
      setRun(data.run);
      setSteps(data.steps || []);
      // Auto-advance to the next pending step (skips completed/skipped)
      const idx = (data.steps || []).findIndex((s) => s.id === target.id);
      const nxt = nextPendingIdx(data.steps || [], idx >= 0 ? idx : activeIdx);
      setActiveIdx(nxt);
      toast.success(status === "done" ? "Step completed" : status === "skipped" ? "Step skipped" : "Step marked");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const submitStep = async (status) => {
    await submitFor(step, status, comment);
    setComment("");
  };

  const cancelRun = async () => {
    if (!confirm("Cancel this SOP run? Your progress will be saved.")) return;
    try { await api.post(`/sop-runs/${runId}/cancel`); toast.success("Run cancelled"); onClose?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const delegate = async () => {
    const uid = window.prompt("User ID to delegate to?");
    if (!uid) return;
    try { await api.post(`/sop-runs/${runId}/delegate`, { to_user_id: uid.trim() }); toast.success("Delegated"); onClose?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/sop-runs/${runId}/comment`, { step_id: step?.id || null, body: comment });
      setComment("");
      toast.success("Comment saved to activity log");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  if (!run) return null;

  if (minimized) {
    return (
      <div
        className="fixed z-50 bg-primary text-primary-foreground border border-primary shadow-lg cursor-pointer"
        style={{ left: pos.x, top: pos.y, width: 320 }}
        onClick={() => setMinimized(false)}
        data-testid="sop-runner-minimized"
      >
        <div className="flex items-center gap-2 px-3 py-2 text-sm">
          <Play className="w-3.5 h-3.5" />
          <span className="truncate flex-1"><strong>{run.sop_title}</strong> · {progress}%</span>
          <Maximize2 className="w-3.5 h-3.5" />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dragRef}
      className="fixed z-50 bg-background border-2 border-primary shadow-2xl flex flex-col"
      style={{ left: pos.x, top: pos.y, width: pos.w, height: pos.h }}
      data-testid="sop-runner-panel"
    >
      {/* Title bar (drag handle) */}
      <div
        className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground cursor-move select-none"
        onMouseDown={onDragStart}
        data-testid="sop-runner-header"
      >
        <GripVertical className="w-3.5 h-3.5 opacity-60" />
        <div className="flex-1 min-w-0">
          <div className="text-xs opacity-80 uppercase tracking-widest">Running SOP</div>
          <div className="text-sm font-semibold truncate">{run.sop_title} <span className="opacity-70 text-[10px] ml-1">{run.sop_version || "v1"}</span></div>
        </div>
        {elapsed != null && (
          <span className="text-[11px] font-mono flex items-center gap-1 opacity-90" data-testid="sop-runner-timer" onMouseDown={(e) => e.stopPropagation()}>
            <Timer className="w-3.5 h-3.5" /> {fmtDuration(elapsed)}
          </span>
        )}
        <div className="flex border border-primary-foreground/40 ml-1" onMouseDown={(e) => e.stopPropagation()}>
          <button
            onClick={() => setMode("step")}
            className={`px-1.5 py-0.5 text-[10px] flex items-center gap-1 ${mode === "step" ? "bg-primary-foreground text-primary" : "opacity-80 hover:opacity-100"}`}
            title="Step mode — one step at a time"
            data-testid="sop-runner-mode-step"
          ><Layers className="w-3 h-3" /> Step</button>
          <button
            onClick={() => setMode("full")}
            className={`px-1.5 py-0.5 text-[10px] flex items-center gap-1 ${mode === "full" ? "bg-primary-foreground text-primary" : "opacity-80 hover:opacity-100"}`}
            title="Full mode — all steps in a table"
            data-testid="sop-runner-mode-full"
          ><List className="w-3 h-3" /> Full</button>
        </div>
        <button onClick={() => setMinimized(true)} className="opacity-80 hover:opacity-100 p-1" title="Minimize" data-testid="sop-runner-minimize">
          <Minus className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="opacity-80 hover:opacity-100 p-1" title="Close" data-testid="sop-runner-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} data-testid="sop-runner-progress" />
      </div>

      {/* Iter 125a — Timesheet auto-logging controls */}
      <TimesheetLogBar
        run={run}
        onChanged={(patch) => setRun((r) => ({ ...r, ...patch }))}
      />

      {mode === "step" ? (
        <>
          {/* Step navigator strip */}
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border overflow-x-auto" data-testid="sop-runner-nav">
            {steps.map((s, i) => {
              const st = s.completion?.status || "pending";
              const color =
                st === "done" ? "bg-emerald-500 text-white" :
                st === "skipped" ? "bg-amber-500 text-white" :
                st === "rejected" ? "bg-red-500 text-white" :
                i === activeIdx ? "bg-primary text-primary-foreground" : "bg-muted text-foreground";
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveIdx(i)}
                  className={`text-[10px] font-mono px-1.5 py-0.5 border border-border ${color}`}
                  title={s.title}
                  data-testid={`sop-runner-step-tab-${i}`}
                >
                  {i + 1}
                </button>
              );
            })}
            <span className="ml-auto text-[10px] text-muted-foreground pr-1" data-testid="sop-runner-count">{doneCount}/{steps.length}</span>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-3 space-y-3" data-testid="sop-runner-body">
            {step ? (
              <>
                {step.blocked_reason && step.completion?.status !== "done" && (
                  <div className="border-l-4 border-amber-500 bg-amber-50 p-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid="sop-runner-blocked">
                    <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div><b>Blocked.</b> {step.blocked_reason}</div>
                  </div>
                )}
                <StepView step={step} onNavigate={(route) => nav(route)} />
              </>
            ) : (
              <div className="text-sm text-muted-foreground p-6 text-center">This SOP has no steps yet.</div>
            )}

            {/* Comment box */}
            <div className="border border-border p-2 bg-muted/20">
              <Label className="label-eyebrow text-[10px]">Add a note (goes to activity log)</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Notes, blockers, or context…"
                className="min-h-[52px] text-xs mt-1"
                data-testid="sop-runner-comment"
              />
              <div className="flex gap-1 mt-1">
                <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" onClick={addComment} data-testid="sop-runner-comment-add">
                  <MessageSquare className="w-3 h-3 mr-1" /> Add note
                </Button>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-1 px-2 py-2 border-t border-border bg-muted/30">
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setActiveIdx((i) => Math.max(0, i - 1))} disabled={activeIdx <= 0} data-testid="sop-runner-prev">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setActiveIdx((i) => Math.min(steps.length - 1, i + 1))} disabled={activeIdx >= steps.length - 1} data-testid="sop-runner-next">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
            <div className="mx-1 h-4 w-px bg-border" />
            {step && (
              <>
                <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" onClick={() => submitStep("skipped")} disabled={busy || step.completion?.status === "done" || !!step.blocked_reason} data-testid="sop-runner-skip">
                  <SkipForward className="w-3 h-3 mr-1" /> Skip
                </Button>
                <Button size="sm" className="rounded-none h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => submitStep("done")} disabled={busy || step.completion?.status === "done" || !!step.blocked_reason} data-testid="sop-runner-done">
                  <Check className="w-3.5 h-3.5 mr-1" /> Mark done
                </Button>
              </>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={delegate} data-testid="sop-runner-delegate">
                <UserPlus className="w-3 h-3 mr-1" /> Delegate
              </Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-[10px] text-red-600" onClick={cancelRun} data-testid="sop-runner-cancel">
                Cancel run
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* FULL MODE — all steps in a table */}
          <div className="flex-1 overflow-auto" data-testid="sop-runner-full-table">
            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-muted/40 text-[9px] uppercase tracking-widest text-foreground/60 font-semibold sticky top-0 border-b border-border">
              <div className="col-span-5">Step</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1 text-right">Time</div>
              <div className="col-span-3">Note</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {steps.map((s, i) => {
              const st = s.completion?.status || "pending";
              const isPending = st === "pending";
              return (
                <div key={s.id} className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-border items-start text-xs ${i === activeIdx ? "bg-primary/5" : ""}`} data-testid={`sop-runner-full-row-${i}`}>
                  <div className="col-span-5 min-w-0">
                    <div className="flex items-start gap-1.5">
                      <span className="font-mono text-[10px] text-muted-foreground shrink-0 mt-0.5">{i + 1}.</span>
                      <div className="min-w-0">
                        <div className={`font-semibold ${["done", "skipped"].includes(st) ? "line-through opacity-60" : ""}`}>{s.title}</div>
                        {s.description && (
                          <details className="mt-0.5">
                            <summary className="text-[10px] text-primary cursor-pointer">instructions</summary>
                            {/<[a-z][\s\S]*>/i.test(s.description)
                              ? <RichTextDisplay html={s.description} className="text-[11px] mt-1" />
                              : <div className="text-[11px] text-muted-foreground whitespace-pre-wrap mt-1">{s.description}</div>}
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <span className={`chip text-[9px] ${st === "done" ? "chip-approved" : st === "skipped" ? "chip-pending" : st === "rejected" ? "chip-rejected" : ""}`}>{st}</span>
                    {isPending && s.blocked_reason && (
                      <div className="text-[9px] text-amber-700 mt-0.5" title={s.blocked_reason}>⏳ blocked</div>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono text-[10px] text-muted-foreground pt-0.5" data-testid={`sop-runner-full-time-${i}`}>
                    {s.completion?.duration_seconds != null ? fmtDuration(s.completion.duration_seconds) : "—"}
                  </div>
                  <div className="col-span-3">
                    {isPending ? (
                      <Input
                        value={rowNotes[s.completion?.id] || ""}
                        onChange={(e) => setRowNotes((n) => ({ ...n, [s.completion?.id]: e.target.value }))}
                        placeholder="Note…"
                        className="h-7 rounded-none text-[11px]"
                        data-testid={`sop-runner-full-note-${i}`}
                      />
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic truncate block pt-1">{s.completion?.comment || ""}</span>
                    )}
                  </div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    {isPending && (
                      <>
                        <Button size="sm" variant="outline" className="rounded-none h-7 w-7 p-0" title={s.blocked_reason || "Skip"} onClick={() => submitFor(s, "skipped", rowNotes[s.completion?.id])} disabled={busy || !!s.blocked_reason} data-testid={`sop-runner-full-skip-${i}`}>
                          <SkipForward className="w-3 h-3" />
                        </Button>
                        <Button size="sm" className="rounded-none h-7 w-7 p-0 bg-emerald-600 hover:bg-emerald-700 text-white" title={s.blocked_reason || "Mark done"} onClick={() => submitFor(s, "done", rowNotes[s.completion?.id])} disabled={busy || !!s.blocked_reason} data-testid={`sop-runner-full-done-${i}`}>
                          <Check className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                    {s.kind === "navigate" && s.config?.route && (
                      <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 p-0" title={s.config?.label || s.config.route} onClick={() => nav(s.config.route)}>
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    )}
                    {s.kind === "external_url" && s.config?.url && (
                      <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 p-0" title={s.config?.label || s.config.url} onClick={() => window.open(s.config.url, "_blank", "noopener,noreferrer")}>
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {steps.length === 0 && <div className="text-sm text-muted-foreground p-6 text-center">This SOP has no steps yet.</div>}
          </div>
          <div className="flex items-center gap-1 px-2 py-2 border-t border-border bg-muted/30">
            <span className="text-[10px] text-muted-foreground" data-testid="sop-runner-full-count">{doneCount}/{steps.length} steps handled</span>
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={delegate} data-testid="sop-runner-delegate-full">
                <UserPlus className="w-3 h-3 mr-1" /> Delegate
              </Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-[10px] text-red-600" onClick={cancelRun} data-testid="sop-runner-cancel-full">
                Cancel run
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Resize handle (bottom-right) */}
      <div
        onMouseDown={onResizeStart}
        className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize"
        style={{ background: "linear-gradient(135deg, transparent 50%, hsl(var(--primary)) 50%)" }}
        data-testid="sop-runner-resize"
        title="Drag to resize"
      />
    </div>
  );
}

// ─────────────────── Step Body (step mode) ───────────────────

function StepView({ step, onNavigate }) {
  const kind = step.kind || "checkbox";
  const isHtml = step.description && /<[a-z][\s\S]*>/i.test(step.description);
  // Iter 124b — auto-open external_url step targets in a new browser tab
  // when the step is first shown. Only pops once per step per session.
  const [popped, setPopped] = useState(false);
  useEffect(() => {
    if (kind !== "external_url") return;
    const cfg = step.config || {};
    if (!cfg.url || cfg.auto_open === false || popped) return;
    try { window.open(cfg.url, "_blank", "noopener,noreferrer"); } catch { /* noop */ }
    setPopped(true);
  }, [kind, step.config, popped]);
  return (
    <div className="space-y-2" data-testid={`sop-runner-step-${step.id}`}>
      <div className="flex items-start gap-2">
        <span className="text-xs font-mono font-bold text-primary shrink-0">{(step.order / 10) || step.order || "•"}.</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold">{step.title}</div>
          {step.description && (
            isHtml
              ? <RichTextDisplay html={step.description} className="text-xs mt-0.5" />
              : <div className="text-xs text-muted-foreground whitespace-pre-wrap mt-0.5">{step.description}</div>
          )}
        </div>
      </div>
      {kind === "navigate" && step.config?.route && (
        <Button size="sm" onClick={() => onNavigate(step.config.route)} className="rounded-none" data-testid={`sop-step-navigate-${step.id}`}>
          <ExternalLink className="w-3.5 h-3.5 mr-1" /> {step.config?.label || `Go: ${step.config.route}`}
        </Button>
      )}
      {kind === "external_url" && step.config?.url && (
        <Button
          size="sm"
          onClick={() => window.open(step.config.url, "_blank", "noopener,noreferrer")}
          className="rounded-none"
          data-testid={`sop-step-external-${step.id}`}
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1" />
          {step.config?.label || "Open link"}
          <span className="ml-2 text-[10px] font-mono opacity-70 truncate max-w-[220px]">{step.config.url}</span>
        </Button>
      )}
      {kind === "embed" && step.config?.url && (
        <iframe
          src={step.config.url}
          className="w-full border border-border"
          style={{ height: step.config?.height || 320 }}
          title={step.title}
          allowFullScreen
          data-testid={`sop-step-embed-${step.id}`}
        />
      )}
      {kind === "video" && step.config?.url && (
        <VideoEmbed url={step.config.url} provider={step.config.provider} testKey={step.id} />
      )}
      {kind === "form" && step.config?.fields?.length > 0 && (
        <FormFields fields={step.config.fields} testKey={step.id} />
      )}
      {kind === "upload" && (
        <div className="border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid={`sop-step-upload-${step.id}`}>
          <div className="flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Upload files then paste their URLs into the note field, or attach via the completion API from an integration.</div>
          <div className="text-[10px] mt-1">min files: {step.config?.min_files || 1}, accept: {step.config?.accept || "any"}</div>
        </div>
      )}
      {step.completion?.status && step.completion.status !== "pending" && (
        <div className="text-[10px] text-muted-foreground border-l-2 border-primary pl-2">
          Last saved: {step.completion.status} · {step.completion.completed_at?.slice(0, 16).replace("T", " ")}
          {step.completion.duration_seconds != null && <> · took <b>{fmtDuration(step.completion.duration_seconds)}</b></>}
        </div>
      )}
    </div>
  );
}

function VideoEmbed({ url, provider, testKey }) {
  let src = url;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (yt) src = `https://www.youtube.com/embed/${yt[1]}`;
  else if (/vimeo\.com\/(\d+)/.test(url)) src = url.replace("vimeo.com/", "player.vimeo.com/video/");
  else if (/loom\.com\/share\//.test(url)) src = url.replace("/share/", "/embed/");

  const isFile = provider === "file" || /\.(mp4|webm|mov|m4v)($|\?)/i.test(url);
  if (isFile) {
    return (
      <video src={url} controls className="w-full max-h-[60vh] bg-black" data-testid={`sop-step-video-${testKey}`} />
    );
  }
  return (
    <iframe
      src={src}
      className="w-full border border-border"
      style={{ height: 320 }}
      title="Step video"
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
      data-testid={`sop-step-video-${testKey}`}
    />
  );
}

function FormFields({ fields, testKey }) {
  const [values, setValues] = useState({});
  return (
    <div className="space-y-2" data-testid={`sop-step-form-${testKey}`}>
      {fields.map((f, i) => {
        const key = f.key || `f_${i}`;
        return (
          <div key={key}>
            <Label className="label-eyebrow text-[10px]">
              {f.label || key}{f.required ? " *" : ""}
            </Label>
            {f.type === "textarea" ? (
              <Textarea value={values[key] || ""} onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))} className="text-xs" data-testid={`sop-form-${testKey}-${key}`} />
            ) : (
              <Input value={values[key] || ""} onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))} type={f.type === "number" ? "number" : "text"} className="h-8 rounded-none text-xs" data-testid={`sop-form-${testKey}-${key}`} />
            )}
          </div>
        );
      })}
      <p className="text-[10px] text-muted-foreground italic">Form values are captured with the next &ldquo;Mark done&rdquo; click.</p>
    </div>
  );
}

// ─────────────── Iter 125a — TimesheetLogBar ───────────────
//
// Compact top-of-panel toggle that lets the assignee pick how the
// running SOP should feed the daily timesheet swimlane.
//   • Off  → nothing happens
//   • Step → one time_entry written for every "Mark done" (start→now)
//   • Run  → one time_entry written when the whole run auto-completes
//
// When linked to a specific task, that task is preselected. When linked
// to a project (or nothing), the user picks a task from the workspace's
// task list.

function TimesheetLogBar({ run, onChanged }) {
  const { activeWorkspaceId } = useAuth();
  const [mode, setMode] = useState(run.time_mode || "none");
  const [taskId, setTaskId] = useState(run.time_task_id || (run.link_kind === "task" ? run.link_id : ""));
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);

  useEffect(() => { setMode(run.time_mode || "none"); }, [run.time_mode]);
  useEffect(() => { setTaskId(run.time_task_id || (run.link_kind === "task" ? run.link_id : "")); }, [run.time_task_id, run.link_kind, run.link_id]);

  useEffect(() => {
    if (!picking || !activeWorkspaceId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/tasks`, {
          params: { status: "in_progress,not_started" },
        });
        setTasks(Array.isArray(data) ? data.slice(0, 300) : []);
      } catch { /* noop */ }
    })();
  }, [picking, activeWorkspaceId]);

  const save = async (nextMode, nextTaskId) => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { time_mode: nextMode, time_task_id: nextMode === "none" ? null : nextTaskId };
      const { data } = await api.patch(`/sop-runs/${run.id}/time-settings`, body);
      onChanged?.(data);
      if (nextMode === "none") toast.info("Timesheet logging: off");
      else toast.success(`Timesheet: logging per ${nextMode}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't update logging"));
    } finally { setBusy(false); }
  };

  const setModeAndSave = (next) => {
    setMode(next);
    if (next === "none") { save("none", null); return; }
    if (!taskId) { setPicking(true); return; }
    save(next, taskId);
  };

  const selectTask = (t) => {
    setTaskId(t.id);
    setPicking(false);
    // If already off, flip to step by default so the pick has an effect.
    save(mode === "none" ? "step" : mode, t.id);
  };

  const chosenTask = useMemo(() => tasks.find((t) => t.id === taskId), [tasks, taskId]);

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border bg-muted/40 text-[11px]" data-testid="sop-runner-timebar">
      <Clock className="w-3 h-3 text-muted-foreground" />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Timesheet</span>
      <div className="flex border border-border" onMouseDown={(e) => e.stopPropagation()}>
        {[
          { key: "none", label: "Off" },
          { key: "step", label: "Per step" },
          { key: "run", label: "Whole run" },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setModeAndSave(opt.key)}
            disabled={busy}
            className={`px-1.5 py-0.5 text-[10px] ${mode === opt.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"} disabled:opacity-50`}
            data-testid={`sop-runner-timemode-${opt.key}`}
          >{opt.label}</button>
        ))}
      </div>
      {mode !== "none" && (
        <button
          type="button"
          className="ml-auto max-w-[180px] truncate text-[10px] hover:underline"
          onClick={() => setPicking((v) => !v)}
          data-testid="sop-runner-timebar-task"
        >
          {taskId ? (
            <>Task: <b>{run.time_task_title || chosenTask?.title || taskId.slice(0, 8)}</b></>
          ) : (
            <span className="text-amber-700">Pick a task →</span>
          )}
        </button>
      )}
      {picking && (
        <div className="absolute right-2 top-16 z-10 w-72 bg-background border border-border shadow-lg max-h-64 overflow-y-auto" onMouseDown={(e) => e.stopPropagation()} data-testid="sop-runner-timebar-picker">
          <div className="p-2 border-b border-border/60 text-[10px] uppercase tracking-widest text-muted-foreground">Choose a task</div>
          {tasks.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground text-center">No open tasks.</div>
          )}
          {tasks.slice(0, 30).map((t) => (
            <button
              key={t.id}
              onClick={() => selectTask(t)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/40 border-b border-border/40 truncate"
              data-testid={`sop-runner-timebar-task-${t.id}`}
            >
              <div className="font-medium truncate">{t.title}</div>
              <div className="text-[9px] text-muted-foreground truncate">{t.project_name || t.category || "—"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
